import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Viewer, Worker, SpecialZoomLevel } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import findLinksPlugin from "./findLinksPlugin";
import { toolbarPlugin } from '@react-pdf-viewer/toolbar';
import { zoomPlugin } from '@react-pdf-viewer/zoom';

import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import { safeGetDocumentParams } from '../pdfOptions';

const MIN_SCALE = 0.25;
const MAX_SCALE = 5;
const WHEEL_ZOOM_SENSITIVITY = 400; // higher = less zoom per wheel tick
const BUTTON_ZOOM_STEP = 1.2; // multiplicative step per +/- click
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_DIST_PX = 30;
const DOUBLE_TAP_ZOOM_LEVEL = 2;

const Paper = ({ documentUrl, dlEnabled }) => {
    const [windowHeight, setWindowHeight] = useState(window.innerHeight);

    const scaleRef = useRef(1);
    const pinchActiveRef = useRef(false);
    const pinchInitialDistanceRef = useRef(0);
    const pinchMidpointRef = useRef({ x: 0, y: 0 });
    const pinchRatioRef = useRef(1);
    const rafIdRef = useRef(null);
    const lastTapRef = useRef({ time: 0, x: 0, y: 0 });
    // Wheel-gesture accumulation (trackpad pinch / ctrl+scroll)
    const wheelPendingRef = useRef(null);
    const wheelTimeoutRef = useRef(null);

    const debounce = (func, wait) => {
        let timeout;
        return (...args) => {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    };

    const updateHeight = () => {
        setWindowHeight(window.innerHeight);
    };

    const debouncedUpdateHeight = debounce(updateHeight, 200);

    useEffect(() => {
        window.addEventListener('resize', debouncedUpdateHeight);
        updateHeight();

        return () => {
            window.removeEventListener('resize', debouncedUpdateHeight);
        };
    }, [debouncedUpdateHeight]);

    const findLinksPluginInstance = useMemo(() => findLinksPlugin(), []);
    const zoomPluginInstance = useMemo(() => zoomPlugin(), []);
    const toolbarPluginInstance = useMemo(() => toolbarPlugin(), []);
    const { CurrentScale } = zoomPluginInstance;
    const isDlEnabled = (dlEnabled === 'true');

    const getScrollEl = () =>
        document.querySelector('.rpv-core__inner-pages');

    const clampScale = (s) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));

    // Smart zoom: change scale while keeping an anchor point fixed on screen.
    // anchorX/Y are viewport (clientX/Y) coordinates; default is the center of the scroll container.
    const zoomAround = (newScale, anchorX, anchorY) => {
        const el = getScrollEl();
        const oldScale = scaleRef.current;

        if (!el || !isFinite(oldScale) || oldScale <= 0) {
            zoomPluginInstance.zoomTo(clampScale(newScale));
            return;
        }

        const targetScale = clampScale(newScale);
        if (Math.abs(targetScale - oldScale) < 0.001) return;

        const rect = el.getBoundingClientRect();
        const ax = anchorX == null ? rect.left + rect.width / 2 : anchorX;
        const ay = anchorY == null ? rect.top + rect.height / 2 : anchorY;
        const relX = ax - rect.left;
        const relY = ay - rect.top;
        const contentX = el.scrollLeft + relX;
        const contentY = el.scrollTop + relY;

        zoomPluginInstance.zoomTo(targetScale);

        // Wait for the library to re-layout at the new scale, then anchor the scroll.
        // Two rAFs handle the case where layout takes an extra frame.
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const ratio = targetScale / oldScale;
                el.scrollLeft = contentX * ratio - relX;
                el.scrollTop = contentY * ratio - relY;
            });
        });
    };

    const fitToPage = () => zoomPluginInstance.zoomTo(SpecialZoomLevel.PageFit);
    const fitToWidth = () => zoomPluginInstance.zoomTo(SpecialZoomLevel.PageWidth);
    const zoomInSmart = () => zoomAround(scaleRef.current * BUTTON_ZOOM_STEP);
    const zoomOutSmart = () => zoomAround(scaleRef.current / BUTTON_ZOOM_STEP);

    const renderToolbar = (Toolbar) => (
        <Toolbar>
            {(slots) => {
                const {
                    CurrentPageInput,
                    Download,
                    EnterFullScreen,
                    GoToNextPage,
                    GoToPreviousPage,
                    NumberOfPages,
                    Print,
                    ShowSearchPopover,
                    Zoom,
                    ZoomIn,
                    ZoomOut,
                    SwitchTheme,
                } = slots;
                return (
                    <div style={{ alignItems: 'center', display: 'flex', width: '100%' }}>
                        <div className="rpv-toolbar__left">
                            <div style={{ padding: '0px 2px' }}>
                                <ShowSearchPopover />
                            </div>
                            <div style={{ padding: '0px 2px' }}>
                                <GoToPreviousPage />
                            </div>
                            <div style={{ padding: '0px 2px', width: '4rem' }}>
                                <CurrentPageInput />
                            </div>
                            <div style={{ padding: '0px 2px', fontSize: "14px", marginLeft: "0.5rem" }}>
                                <NumberOfPages />
                            </div>
                            <div style={{ padding: '0px 2px' }}>
                                <GoToNextPage />
                            </div>
                        </div>
                        <div className="rpv-toolbar__center">
                            <button
                                type="button"
                                onClick={zoomOutSmart}
                                title="Zoom out"
                                aria-label="Zoom out"
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="7" />
                                    <line x1="21" y1="21" x2="16.5" y2="16.5" />
                                    <line x1="8" y1="11" x2="14" y2="11" />
                                </svg>
                            </button>
                            <div style={{ padding: '0px 2px' }}>
                                <Zoom />
                            </div>
                            <button
                                type="button"
                                onClick={zoomInSmart}
                                title="Zoom in"
                                aria-label="Zoom in"
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="7" />
                                    <line x1="21" y1="21" x2="16.5" y2="16.5" />
                                    <line x1="11" y1="8" x2="11" y2="14" />
                                    <line x1="8" y1="11" x2="14" y2="11" />
                                </svg>
                            </button>
                            <button
                                type="button"
                                onClick={fitToPage}
                                title="Fit page"
                                aria-label="Fit page"
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '4px 6px',
                                    marginLeft: '4px',
                                    borderRadius: '4px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="5" y="3" width="14" height="18" rx="1" />
                                    <path d="M9 7h6M9 12h6M9 17h4" />
                                </svg>
                            </button>
                            <button
                                type="button"
                                onClick={fitToWidth}
                                title="Fit width"
                                aria-label="Fit width"
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '4px 6px',
                                    marginLeft: '2px',
                                    borderRadius: '4px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M3 12h18" />
                                    <path d="M7 8l-4 4 4 4" />
                                    <path d="M17 8l4 4-4 4" />
                                </svg>
                            </button>
                        </div>
                        <div className="rpv-toolbar__right">
                            {isDlEnabled &&
                                <div style={{ padding: '0px 2px' }}>
                                    <Download />
                                </div>
                            }
                            {isDlEnabled &&
                                <div style={{ padding: '0px 2px' }}>
                                    <Print />
                                </div>
                            }
                            <div style={{ padding: '0px 2px', marginLeft: 'auto' }}>
                                <EnterFullScreen />
                            </div>
                            <div style={{ padding: '0px 2px', marginLeft: 'auto' }}>
                                <SwitchTheme />
                            </div>
                        </div>
                    </div>
                );
            }}
        </Toolbar>
    );

    const defaultLayoutPluginInstance = defaultLayoutPlugin({
        renderToolbar,
        sidebarTabs: (defaultTabs) => [],
    });

    useEffect(() => {
        const content = document.getElementById('content');
        if (!content) return;

        // Intercept wheel events in capture phase so the library's own (buggy) ctrl+wheel handler
        // never fires. On macOS trackpad pinch also sets ctrlKey=true, so this catches both.
        //
        // Two-phase: during the gesture we only apply a CSS transform (GPU path, instant), and
        // debounce-commit the real scale via zoomTo() once wheel events stop arriving. This is
        // the difference between "smooth" and "chunky" — calling zoomTo every tick forces a full
        // PDF re-rasterization per tick which the library cannot keep up with.
        const commitWheelZoom = () => {
            const pending = wheelPendingRef.current;
            wheelPendingRef.current = null;
            wheelTimeoutRef.current = null;
            if (!pending) return;

            const el = content.querySelector('.rpv-core__inner-pages > div');
            if (el) {
                el.style.transition = 'none';
                el.style.transform = '';
                el.style.transformOrigin = '';
                el.style.willChange = '';
            }
            zoomAround(pending.targetScale, pending.anchorX, pending.anchorY);
        };

        const handleWheel = (e) => {
            if (!e.ctrlKey && !e.metaKey) return;
            e.preventDefault();
            e.stopPropagation();

            const el = content.querySelector('.rpv-core__inner-pages > div');
            if (!el) return;

            const factor = Math.exp(-e.deltaY / WHEEL_ZOOM_SENSITIVITY);

            // First tick of a new gesture: snapshot starting scale and anchor
            if (wheelPendingRef.current === null) {
                wheelPendingRef.current = {
                    startScale: scaleRef.current,
                    targetScale: scaleRef.current,
                    anchorX: e.clientX,
                    anchorY: e.clientY,
                };
            }

            const pending = wheelPendingRef.current;
            pending.targetScale = clampScale(pending.targetScale * factor);
            const ratio = pending.targetScale / pending.startScale;

            // Apply CSS transform immediately — pure compositor work, runs at 60fps
            const rect = el.getBoundingClientRect();
            const originX = pending.anchorX - rect.left;
            const originY = pending.anchorY - rect.top;
            el.style.willChange = 'transform';
            el.style.transition = 'none';
            el.style.transformOrigin = `${originX}px ${originY}px`;
            el.style.transform = `scale(${ratio})`;

            // Reset debounce timer — commit 150ms after the last wheel event
            if (wheelTimeoutRef.current !== null) {
                clearTimeout(wheelTimeoutRef.current);
            }
            wheelTimeoutRef.current = setTimeout(commitWheelZoom, 150);
        };
        content.addEventListener('wheel', handleWheel, {
            passive: false,
            capture: true,
        });

        const getTransformTarget = () =>
            content.querySelector('.rpv-core__inner-pages > div');

        const getDistance = (t1, t2) => {
            const dx = t2.clientX - t1.clientX;
            const dy = t2.clientY - t1.clientY;
            return Math.hypot(dx, dy);
        };

        const getMidpoint = (t1, t2) => ({
            x: (t1.clientX + t2.clientX) / 2,
            y: (t1.clientY + t2.clientY) / 2,
        });

        const clampScale = (s) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));

        const clearPinchTransform = () => {
            const el = getTransformTarget();
            if (!el) return;
            el.style.transition = 'none';
            el.style.transform = '';
            el.style.transformOrigin = '';
        };

        const handleTouchStart = (event) => {
            if (event.touches.length === 2) {
                // Start of a pinch
                pinchActiveRef.current = true;
                pinchInitialDistanceRef.current = getDistance(
                    event.touches[0],
                    event.touches[1]
                );
                pinchMidpointRef.current = getMidpoint(
                    event.touches[0],
                    event.touches[1]
                );
                pinchRatioRef.current = 1;
                // Reset any pending double-tap
                lastTapRef.current = { time: 0, x: 0, y: 0 };
                event.preventDefault();
                return;
            }

            if (event.touches.length === 1) {
                // Double-tap detection
                const now = Date.now();
                const touch = event.touches[0];
                const last = lastTapRef.current;
                const dist = Math.hypot(
                    touch.clientX - last.x,
                    touch.clientY - last.y
                );

                if (
                    now - last.time < DOUBLE_TAP_MS &&
                    dist < DOUBLE_TAP_DIST_PX
                ) {
                    // Toggle between fit-page and 2x
                    if (scaleRef.current > 1.1) {
                        zoomPluginInstance.zoomTo(SpecialZoomLevel.PageFit);
                    } else {
                        zoomPluginInstance.zoomTo(DOUBLE_TAP_ZOOM_LEVEL);
                    }
                    lastTapRef.current = { time: 0, x: 0, y: 0 };
                    event.preventDefault();
                } else {
                    lastTapRef.current = {
                        time: now,
                        x: touch.clientX,
                        y: touch.clientY,
                    };
                }
            }
        };

        const handleTouchMove = (event) => {
            if (!pinchActiveRef.current || event.touches.length !== 2) return;
            event.preventDefault();

            const distance = getDistance(event.touches[0], event.touches[1]);
            if (pinchInitialDistanceRef.current <= 0) return;
            const rawRatio = distance / pinchInitialDistanceRef.current;

            const currentScale = scaleRef.current;
            const targetScale = clampScale(currentScale * rawRatio);
            const clampedRatio = currentScale > 0 ? targetScale / currentScale : 1;
            pinchRatioRef.current = clampedRatio;

            if (rafIdRef.current !== null) {
                cancelAnimationFrame(rafIdRef.current);
            }
            rafIdRef.current = requestAnimationFrame(() => {
                rafIdRef.current = null;
                const el = getTransformTarget();
                if (!el) return;
                const rect = el.getBoundingClientRect();
                const originX = pinchMidpointRef.current.x - rect.left;
                const originY = pinchMidpointRef.current.y - rect.top;
                el.style.transition = 'none';
                el.style.transformOrigin = `${originX}px ${originY}px`;
                el.style.transform = `scale(${clampedRatio})`;
            });
        };

        const handleTouchEnd = (event) => {
            if (!pinchActiveRef.current) return;
            // Still pinching with 2+ fingers; wait for next release
            if (event.touches.length >= 2) return;

            pinchActiveRef.current = false;
            if (rafIdRef.current !== null) {
                cancelAnimationFrame(rafIdRef.current);
                rafIdRef.current = null;
            }

            const finalScale = clampScale(
                scaleRef.current * pinchRatioRef.current
            );

            // Drop CSS transform and commit final scale — library re-renders crisp
            clearPinchTransform();
            zoomPluginInstance.zoomTo(finalScale);
        };

        const handleTouchCancel = () => {
            pinchActiveRef.current = false;
            if (rafIdRef.current !== null) {
                cancelAnimationFrame(rafIdRef.current);
                rafIdRef.current = null;
            }
            clearPinchTransform();
        };

        content.addEventListener('touchstart', handleTouchStart, {
            passive: false,
        });
        content.addEventListener('touchmove', handleTouchMove, {
            passive: false,
        });
        content.addEventListener('touchend', handleTouchEnd, {
            passive: false,
        });
        content.addEventListener('touchcancel', handleTouchCancel, {
            passive: false,
        });

        return () => {
            if (wheelTimeoutRef.current !== null) {
                clearTimeout(wheelTimeoutRef.current);
                wheelTimeoutRef.current = null;
            }
            content.removeEventListener('wheel', handleWheel, { capture: true });
            content.removeEventListener('touchstart', handleTouchStart);
            content.removeEventListener('touchmove', handleTouchMove);
            content.removeEventListener('touchend', handleTouchEnd);
            content.removeEventListener('touchcancel', handleTouchCancel);
        };
    }, [zoomPluginInstance]);

    return (
        <Worker workerUrl="https://pdfdeck.com/static/website/js/pdfworker.js">
            <div
                style={{
                    position: 'relative',
                    top: '0',
                    bottom: '0',
                    left: '0',
                    right: '0',
                    border: "none",
                    height: `${windowHeight}px`,
                    width: "100%",
                }}
            >
                <div id="content" style={{ position: "relative", height: "100%", width: "100%", touchAction: "pan-y" }}>
                    <Viewer
                        fileUrl={documentUrl}
                        transformGetDocumentParams={safeGetDocumentParams}
                        plugins={[findLinksPluginInstance, defaultLayoutPluginInstance, zoomPluginInstance]}
                        defaultScale={SpecialZoomLevel.PageFit}
                    />
                    <CurrentScale>
                        {(props) => {
                            scaleRef.current = props.scale;
                            return <></>;
                        }}
                    </CurrentScale>
                </div>
            </div>
        </Worker>
    );
};

export default Paper;
